import React, {useEffect, useRef} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import CommerciumLoader from 'src/utils/CommerciumLoader'
import useAppToast from 'src/hooks/useAppToast'

const REQUEST_TIMEOUT_MS = 10000
const AUTH_REDIRECT_PATH = '/auth'
const ERROR_TOAST_MESSAGE = 'Connection failed. Please try again or contact support.'

type AppOauthError = {
  code: string
  message: string
  status?: number
}

export function buildAppOauthQueryString(platformName: string, rawSearch: string): string {
  const platformParam = `platform_name=${encodeURIComponent(platformName)}`
  const trimmedSearch = rawSearch.trim()

  if (!trimmedSearch || trimmedSearch === '?') {
    return platformParam
  }

  const rest = trimmedSearch.startsWith('?') ? trimmedSearch.slice(1) : trimmedSearch
  const filtered = rest
    .split('&')
    .filter((pair) => pair.length > 0 && !pair.startsWith('platform_name='))
    .join('&')

  return filtered ? `${platformParam}&${filtered}` : platformParam
}

export function extractAppOauthRedirectUrl(response: unknown): string | null {
  if (!response || typeof response !== 'object') {
    return null
  }

  const payload = response as Record<string, unknown>
  const data = payload.data

  if (data && typeof data === 'object') {
    const dataRecord = data as Record<string, unknown>
    if (typeof dataRecord.redirect_url === 'string' && dataRecord.redirect_url.trim()) {
      return dataRecord.redirect_url.trim()
    }
    if (typeof dataRecord.url === 'string' && dataRecord.url.trim()) {
      return dataRecord.url.trim()
    }
  }

  if (typeof payload.redirect_url === 'string' && payload.redirect_url.trim()) {
    return payload.redirect_url.trim()
  }

  if (typeof payload.url === 'string' && payload.url.trim()) {
    return payload.url.trim()
  }

  return null
}

function normalizeApiBaseUrl(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '')
}

function normalizeRawSearch(rawSearch: string): string {
  const trimmedSearch = rawSearch.trim()
  return trimmedSearch.startsWith('?') ? trimmedSearch.slice(1) : trimmedSearch
}

export function buildAppOauthRequestUrl(
  apiBaseUrl: string,
  platformName: string,
  rawSearch: string
): string {
  return `${normalizeApiBaseUrl(apiBaseUrl)}/api/app-oauth/url?${buildAppOauthQueryString(platformName, rawSearch)}`
}

export function buildAppOauthCallbackRequestUrl(
  apiBaseUrl: string,
  rawSearch: string
): string {
  const queryString = normalizeRawSearch(rawSearch)
  return `${normalizeApiBaseUrl(apiBaseUrl)}/api/app-oauth/callback?${queryString}`
}

async function fetchAppOauthRedirect(fullUrl: string, signal?: AbortSignal): Promise<string> {
  const apiBaseUrl = process.env.REACT_APP_API_URL

  if (!apiBaseUrl) {
    throw {
      code: 'CONFIG_ERROR',
      message: 'Missing REACT_APP_API_URL',
    } as AppOauthError
  }

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      signal: signal ?? controller.signal,
    })

    clearTimeout(timeout)

    if (!response.ok) {
      throw {
        code: 'REQUEST_FAILED',
        message: `Request failed with status ${response.status}`,
        status: response.status,
      } as AppOauthError
    }

    const json = await response.json()
    const redirectUrl = extractAppOauthRedirectUrl(json)

    if (!redirectUrl) {
      throw {
        code: 'INVALID_RESPONSE',
        message: 'Backend response did not include a redirect URL',
      } as AppOauthError
    }

    return redirectUrl
  } catch (err: unknown) {
    clearTimeout(timeout)

    if (err && typeof err === 'object' && 'code' in err) {
      throw err
    }

    const isAbort = err instanceof DOMException && err.name === 'AbortError'

    throw {
      code: isAbort ? 'REQUEST_TIMEOUT' : 'REQUEST_FAILED',
      message: isAbort
        ? `Request timed out after ${REQUEST_TIMEOUT_MS / 1000} seconds`
        : 'App OAuth request failed',
    } as AppOauthError
  }
}

export async function callAppOauthUrl(
  platformName: string,
  rawSearch: string,
  signal?: AbortSignal
): Promise<string> {
  const apiBaseUrl = process.env.REACT_APP_API_URL

  if (!apiBaseUrl) {
    throw {
      code: 'CONFIG_ERROR',
      message: 'Missing REACT_APP_API_URL',
    } as AppOauthError
  }

  return fetchAppOauthRedirect(
    buildAppOauthRequestUrl(apiBaseUrl, platformName, rawSearch),
    signal
  )
}

export async function callAppOauthCallback(
  rawSearch: string,
  signal?: AbortSignal
): Promise<string> {
  const apiBaseUrl = process.env.REACT_APP_API_URL

  if (!apiBaseUrl) {
    throw {
      code: 'CONFIG_ERROR',
      message: 'Missing REACT_APP_API_URL',
    } as AppOauthError
  }

  const queryString = normalizeRawSearch(rawSearch)

  if (!queryString) {
    throw {
      code: 'INVALID_CALLBACK_URL',
      message: 'Callback URL did not contain query parameters',
    } as AppOauthError
  }

  return fetchAppOauthRedirect(
    buildAppOauthCallbackRequestUrl(apiBaseUrl, rawSearch),
    signal
  )
}

export const AppOauthPage: React.FC = () => {
  const {platform_name: platformNameParam} = useParams<{platform_name: string}>()
  const {pathname} = useLocation()
  const isCallbackRoute = pathname === '/callback' || pathname.endsWith('/callback')
  const {errorToast} = useAppToast()
  const initialized = useRef(false)

  useEffect(() => {
    document.title = 'Connecting... | MarketConnect'
  }, [])

  useEffect(() => {
    if (initialized.current) {
      return
    }
    initialized.current = true

    const abortController = new AbortController()

    const redirectToAuth = () => {
      errorToast(ERROR_TOAST_MESSAGE)
      window.location.href = AUTH_REDIRECT_PATH
    }

    const startFlow = async () => {
      try {
        let redirectUrl: string

        if (isCallbackRoute) {
          redirectUrl = await callAppOauthCallback(
            window.location.search,
            abortController.signal
          )
        } else {
          const platformName = platformNameParam?.trim()

          if (!platformName) {
            redirectToAuth()
            return
          }

          redirectUrl = await callAppOauthUrl(
            platformName,
            window.location.search,
            abortController.signal
          )
        }

        window.location.href = redirectUrl
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return
        }

        redirectToAuth()
      }
    }

    startFlow()

    return () => {
      abortController.abort()
    }
  }, [errorToast, isCallbackRoute, platformNameParam])

  return (
    <div className='h-screen w-screen'>
      <CommerciumLoader
        message='Connecting to platform, please wait...'
        subMessage='You will be redirected automatically.'
      />
    </div>
  )
}
